import { Fragment, useMemo, useRef } from 'react';
import { Button, Grid } from '@looker/components';
import { isAfter } from 'date-fns';
import Highcharts from 'highcharts/highstock';

import { Loading } from '@/components/ui/old/Loading';
import { useCurrentAsset } from '@/hooks/use-current-asset';

import { GetChartData } from '../api/queries/get-chart-data';
import { GetLevels } from '../api/queries/get-levels';
import { ListIds } from '../api/queries/list-ids';
import type { BatteryLevel } from '../entities/battery-level';
import { ChartType } from '../entities/chart-type';
import { type Filters, type RequestFilters } from '../entities/filters';
import { useAutoNavigateToData } from '../hooks/use-auto-navigate-to-data';
import { isAtLowestLevel } from '../hooks/use-is-at-lowest-level';
import { useDeriveCurrentNodes } from '../utils/derive-node';
import { getCurrentLevelInfo } from '../utils/parent-id-logic';

import { AggregatedLevelDataBanner } from './aggregated-level-data-banner';
import { Chart } from './chart';
import { ChartSkeleton } from './chart-skeleton';
import { EmptyChartState } from './empty-chart-state';
import { type Domain } from './filters/domain-toggle';
import { FindingDataState } from './finding-data-state';
import { LoadingProgress } from './loading-progress';
import { NoDataAtLevelState } from './no-data-at-level-state';

const chartTypesOrder: ChartType[] = [
  ChartType.VOLTAGE,
  ChartType.CURRENT,
  ChartType.SOC,
  ChartType.TEMPERATURE,
  ChartType.CELL_VOLTAGE,
  ChartType.POWER,
  ChartType.POWER_FACTOR,
  ChartType.ENERGY,
  ChartType.FREQUENCY,
  ChartType.UNITS_ONLINE,
  ChartType.RACKS_ONLINE,
  ChartType.HS_CONTROL_AGC,
];

export type DataExplorerResultProps = {
  filters: Filters;
  domain: Domain;
  onUpdateFilters: (updated: Partial<Filters>) => void;
};

export function DataExplorerResult({
  filters,
  domain,
  onUpdateFilters,
}: DataExplorerResultProps) {
  const asset = useCurrentAsset();
  const getLevelsData = GetLevels.useQuery({ retry: 3 });

  // Determine which level and parent ID to query for nodes data
  const currentLevelInfo = useMemo(() => {
    if (!getLevelsData.data || !asset?.accureId) return null;

    const levelsMap = (getLevelsData.data[domain] ?? {}) as Record<
      BatteryLevel,
      string
    >;
    const levelsOrder = Object.keys(levelsMap) as BatteryLevel[];
    const currentFilters =
      domain === 'battery' ? filters.batteryFilters : filters.pcsFilters;

    return getCurrentLevelInfo(currentFilters, levelsOrder, asset.accureId);
  }, [filters, domain, getLevelsData.data, asset?.accureId]);

  // Get nodes data for the current level to preserve labels
  const nodesData = ListIds.useQuery({
    level: currentLevelInfo?.level || 'system',
    parentId: currentLevelInfo?.parentId,
    retry: 3,
  });

  // Derive the current nodes using the hook that supports multi-select
  const currentNodes = useDeriveCurrentNodes(
    filters,
    domain,
    nodesData.data?.ids,
  );

  // Check if we're NOT at the lowest level (site-level, PCS, or intermediate levels)
  // Used to show aggregated data banner and anomaly detection hints
  // For PCS domain: always show banner since PCS data is never "processed" like battery cell data
  // For battery domain: show banner when not at the lowest available level
  const isNotAtLowestLevel =
    currentNodes.length > 0 &&
    (domain === 'pcs' || !isAtLowestLevel(filters, domain, getLevelsData.data));

  const requestFilters = useMemo<RequestFilters | undefined>(() => {
    const { coreFilters } = filters;

    if (isAfter(coreFilters.from, coreFilters.to)) {
      return undefined;
    }

    // Use the derived nodes which now includes multi-select support
    if (!currentNodes || currentNodes.length === 0) {
      return undefined;
    }

    return {
      nodes: currentNodes,
      chartTypes: coreFilters.chartTypes,
      from: coreFilters.from,
      to: coreFilters.to,
      filterNoise: coreFilters.filterNoise,
    };
  }, [filters, currentNodes]);

  const chartData = GetChartData.useQuery(requestFilters, { retry: 3 });

  // Check if all queries are loading
  const isLoadingChartData = chartData.every(({ query }) => query.isLoading);

  // Check if all queries have empty data (successful but no results)
  const allQueriesEmpty = chartData.every(({ query }) => {
    if (query.isLoading || query.isError) return false;
    const data = query.data;
    return !data || Object.keys(data).length === 0;
  });

  // Capture initial URL state - if URL had params on mount, user navigated here deliberately
  const initialUrlHadParamsRef = useRef(
    typeof window !== 'undefined' && window.location.search.length > 0,
  );

  // Auto-navigate to first level with data when system-level data is empty
  // Must be called after chartData queries so we know if system level has data
  const { isSearching, searchingLevel } = useAutoNavigateToData({
    filters,
    domain,
    levelsData: getLevelsData.data,
    onUpdateFilters,
    systemLevelDataEmpty: allQueriesEmpty && !isLoadingChartData,
    urlHasExplicitParams: initialUrlHadParamsRef.current,
    isLoadingChartData,
  });

  // Calculate loading statistics
  const loadingStats = useMemo(() => {
    if (!requestFilters) return { loadingCount: 0, totalCount: 0 };

    const selectedChartTypes = requestFilters.chartTypes;
    const loadingCount = chartData.filter(
      ({ query }) => query.isLoading,
    ).length;
    const totalQueries = chartData.length;

    return {
      loadingCount,
      totalCount: totalQueries,
      selectedChartTypesCount: selectedChartTypes.length,
    };
  }, [chartData, requestFilters]);

  // Check if all queries failed (real errors, not empty data)
  const allQueriesFailed = chartData.every(({ query }) => query.isError);

  if (allQueriesFailed) {
    // Real error - show generic error message
    return (
      <div className="px-[60px] py-[40px]" data-testid="data-explorer-results">
        <div className="rounded-md border border-red-200 bg-red-50 p-6">
          <h3 className="text-base font-semibold text-red-900">
            Something went wrong
          </h3>
          <p className="mt-2 text-sm text-red-800">
            We encountered an error while loading the data. Please try again or
            contact support if the problem persists.
          </p>
        </div>
      </div>
    );
  }

  // Check if all queries are loading or in error state
  const allLoadingOrError = chartData.every(
    ({ query }) => query.isLoading || query.isError,
  );

  if (allLoadingOrError && !allQueriesFailed) {
    return (
      <Grid columns={1} gap="u2" className="dataExplorerWrapper">
        <div className="flex h-screen items-center justify-center">
          <Loading />
        </div>
      </Grid>
    );
  }

  function handleResetZoom() {
    for (const chart of Highcharts.charts) {
      if (!chart) {
        continue;
      }

      const axis = chart.xAxis.at(0);

      if (!axis) {
        continue;
      }

      axis.setExtremes(undefined, undefined, true);
    }
  }

  // Show searching state when auto-navigating to find data
  if (isSearching) {
    return (
      <div className="px-[60px] py-[40px]" data-testid="data-explorer-results">
        <FindingDataState levelName={searchingLevel} />
      </div>
    );
  }

  // Show empty state if all queries returned empty data
  if (
    allQueriesEmpty &&
    currentNodes &&
    currentNodes.length > 0 &&
    requestFilters
  ) {
    return (
      <div className="px-[60px] py-[40px]" data-testid="data-explorer-results">
        <NoDataAtLevelState />
      </div>
    );
  }

  const anyChartHasData = chartData.some(
    ({ query }) =>
      !query.isLoading &&
      !query.isError &&
      query.data &&
      Object.values(query.data).some(
        (arr) => Array.isArray(arr) && arr.length > 0,
      ),
  );
  const allChartsEmpty =
    !anyChartHasData &&
    chartData.every(({ query }) => !query.isLoading && !query.isError);

  return (
    <div className="px-[60px] py-[40px]" data-testid="data-explorer-results">
      <div className="mb-5 flex items-center justify-between">
        <div className="flex-1">
          <LoadingProgress
            loadingCount={loadingStats.loadingCount}
            totalCount={loadingStats.totalCount}
          />
        </div>
        {anyChartHasData ? (
          <Button className="zoom-btn" onClick={handleResetZoom}>
            Reset Zoom
          </Button>
        ) : null}
      </div>
      {isNotAtLowestLevel && currentNodes[0]?.level ? (
        <AggregatedLevelDataBanner level={currentNodes[0].level} />
      ) : null}
      {allChartsEmpty && requestFilters ? (
        <div className="mb-5">
          <EmptyChartState
            chartType={ChartType.VOLTAGE}
            from={requestFilters.from}
            to={requestFilters.to}
            filterNoise={requestFilters.filterNoise}
            onUpdateFilters={(partial) => onUpdateFilters(partial)}
          />
        </div>
      ) : null}
      <div className="charts-wrapper space-y-5">
        {chartTypesOrder.map((chartType) => {
          // Check if this chart type is selected
          const isSelected = requestFilters?.chartTypes.includes(chartType);
          if (!isSelected) return null;

          // Check if we have actual data points for this chart type from ANY query
          const hasData = chartData.some(({ query }) => {
            const chartTypeData = query.data?.[chartType];
            return chartTypeData && chartTypeData.length > 0;
          });

          // Check if this chart type was requested (exists in response, even if empty)
          const wasRequested = chartData.some(
            ({ query }) => query.data && chartType in query.data,
          );

          // Check if at least one query is currently loading
          const anyQueryLoading = chartData.some(
            ({ query }) => query.isLoading,
          );

          // Only show skeleton when:
          // 1. We have queries and at least one is actively loading
          // 2. No data yet for this chart type
          // 3. User has made an explicit chart selection (not ALL chart types selected)
          //    This prevents "flash" of skeleton during initial state sync
          const allChartTypesCount = Object.values(ChartType).length;
          const hasExplicitSelection =
            requestFilters?.chartTypes &&
            requestFilters.chartTypes.length < allChartTypesCount;

          if (
            chartData.length > 0 &&
            anyQueryLoading &&
            !hasData &&
            hasExplicitSelection
          ) {
            return <ChartSkeleton key={chartType} chartType={chartType} />;
          }

          // Show chart if we have data (even partial) - it will update as more data arrives
          // The LoadingProgress indicator shows overall fetch status
          if (hasData && requestFilters) {
            return (
              <Fragment key={chartType}>
                <Chart
                  chartType={chartType}
                  from={requestFilters.from}
                  to={requestFilters.to}
                  filterNoise={requestFilters.filterNoise}
                  requests={chartData}
                  onUpdateFilters={onUpdateFilters}
                  showAnomalyHints={isNotAtLowestLevel}
                />
              </Fragment>
            );
          }

          // Chart selected but no data available - show inline empty state
          // This happens when backend filters out all-zero or all-NaN columns
          // Only show per-chart empty state if SOME other charts have data
          // (if ALL charts are empty, the global empty state above handles it)
          const allQueriesFinished = chartData.every(
            ({ query }) => !query.isLoading,
          );

          // Show per-chart empty state when:
          // 1. All queries finished loading
          // 2. At least one chart has data (not all empty)
          // 3. This chart was requested (exists in response) but has no data
          if (
            allQueriesFinished &&
            !allChartsEmpty &&
            wasRequested &&
            requestFilters
          ) {
            return (
              <EmptyChartState
                key={chartType}
                chartType={chartType}
                from={requestFilters.from}
                to={requestFilters.to}
                filterNoise={requestFilters.filterNoise}
                onUpdateFilters={onUpdateFilters}
                compact
              />
            );
          }

          return null;
        })}
      </div>
    </div>
  );
}
